/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { ActionType, CreateOnePassActionKernelV2 } from 'client-code/actions';

import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { SharedPrefConstants, SharedPreferences } from '../../../core/storage';
import { TOverlayOptions } from '../../../core/types';
import { DynamicOptionsEngine } from '../../../core/utils';
import { hideOverlayDialog, showOverlayDialog } from '../../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../../overlaydialog/overlaydialogprops';
import { setScreenTaskController } from '../../screens/screens.reducers';
import {
    OnePassOptionsOverlayType,
    showOnePassOptionsOverlay,
} from '../onepassoptionsoverlay/onepassoptionsoverlay.reducers';

const GLOBAL_MOUSE_TRAP = 'HideCreateOnePassOverlay';

const TAG = 'createonepass';

export const prepareCreateOnePassOverlay = () => (dispatch: Function) => {
    const sharedPreferences = new SharedPreferences(SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY);
    const onePassAction = sharedPreferences.getJson(
        SharedPrefConstants.CREATE_ONE_PASS_ACTION,
    ) as any;
    if (onePassAction.actionType === ActionType.MultiOnePassAction) {
        dispatch(showCreateOnePassOverlay(onePassAction.kernel.actions.actions));
    } else {
        dispatch(
            showOnePassOptionsOverlay(
                OnePassOptionsOverlayType.CREATE,
                `${onePassAction.title}${onePassAction.postfixTitle ? ` (${onePassAction.postfixTitle})` : ''}`,
                (onePassAction.kernel as CreateOnePassActionKernelV2).unifiedCollectionId,
                (onePassAction.kernel as CreateOnePassActionKernelV2).channelId,
                (onePassAction.kernel as CreateOnePassActionKernelV2).contentId,
            ),
        );
    }
};

// TODO: assign type to actions when all kernels are clubbed
export const showCreateOnePassOverlay = (actions: any[]) => (dispatch: Function) => {
    const overlapOptions: TOverlayOptions[] | undefined = actions?.map(
        (action): TOverlayOptions => {
            return {
                text: `${action.title}${action.postfixTitle ? ` (${action.postfixTitle})` : ''}`,
                value: (action.kernel as CreateOnePassActionKernelV2).unifiedCollectionId,
            };
        },
    );
    const overlayProps: IOverlayDialog = {
        hasDimmer: true,
        hasSeperator: false,
        mouseTrapValue: GLOBAL_MOUSE_TRAP,
        options: overlapOptions,
        title: Locale.getString(LocaleKeys.CREATE_ONEPASS_OVERLAY_TITLE),
    };
    if (NetworkManager.isNetworkDisconnected()) {
        Log.i(TAG, 'Internet disconnected');
        return;
    }
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS));
    dispatch(showOverlayDialog(overlayProps));
};

export const onKeyOkCreateOnePassOverlay = () => (dispatch: Function, getState: Function) => {
    const { overlayDialog, overlayHighlight } = getState();
    const { verticalPosition } = overlayHighlight;
    const { uiProperties } = overlayDialog;
    const { options } = uiProperties;
    const selectedOption = options[verticalPosition];
    Log.i(TAG, 'one pass selected: ', selectedOption);
    dispatch(hideOverlayDialog());
    dispatch(
        showOnePassOptionsOverlay(
            OnePassOptionsOverlayType.CREATE,
            selectedOption.text,
            selectedOption.value,
        ),
    );
};

/**
 * mouse operations
 */
export const onMouseClickCreateOnePassOverlay = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;

    if (value === GLOBAL_MOUSE_TRAP) {
        dispatch(hideOverlayDialog());
        new DynamicOptionsEngine().clearOnePassOptions();
    } else dispatch(onKeyOkCreateOnePassOverlay());
};
